import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import { caseStudies } from '../mockData';

const highlights = [
  { v: '250%', l: 'Median efficiency improvement' },
  { v: '42d',  l: 'Average certification cycle' },
  { v: '15%',  l: 'First-year real-estate appreciation' },
  { v: '48K+', l: 'Clients served globally' }
];

const CaseStudiesPage = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Case Studies · Lockene',
    url: 'https://lockene.info/case-studies',
    hasPart: caseStudies.map((cs) => ({
      '@type': 'Article',
      headline: cs.title,
      description: cs.excerpt,
      datePublished: cs.date,
      articleSection: cs.category
    }))
  };

  return (
    <>
      <Seo
        title="Case Studies · Lockene"
        description="Documented outcomes from Lockene engagements across AI CRM, government compliance, real estate and sports hospitality."
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="bg-brand-alabaster pt-28 md:pt-36 pb-16" aria-labelledby="cs-heading" data-testid="cs-hero">
        <div className="container-editorial">
          <div className="border-t border-brand-hairline mb-14" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="hairline" aria-hidden />
                <span className="eyebrow">Case Studies</span>
              </div>
              <h1 id="cs-heading" className="font-display text-5xl md:text-7xl leading-[1.03] tracking-tight text-brand-oxford">
                Documented outcomes. Reproducibly.
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg text-brand-slate leading-relaxed">
                Every mandate we run is measurable. Below is a small selection — the full set is available under NDA
                to prospective clients on request.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="bg-white border-y border-brand-hairline py-14" aria-label="Aggregate outcomes">
        <div className="container-editorial grid grid-cols-2 md:grid-cols-4 gap-y-8">
          {highlights.map((h, i) => (
            <div key={h.l} className={i !== 0 ? 'md:border-l md:border-brand-hairline md:pl-8' : ''}>
              <div className="font-display text-4xl md:text-5xl text-brand-oxford leading-none">{h.v}</div>
              <div className="mt-3 eyebrow">{h.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Studies grid */}
      <section className="section bg-brand-alabaster" aria-labelledby="cs-list-heading">
        <div className="container-editorial">
          <Reveal className="max-w-2xl mb-14">
            <h2 id="cs-list-heading" className="font-display text-4xl md:text-5xl leading-tight text-brand-oxford">
              Selected mandates
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {caseStudies.map((cs, i) => (
              <Reveal key={cs.id} delay={i * 70}>
                <Link
                  to={`/case-studies/${cs.slug}`}
                  data-testid={`cs-card-${cs.slug}`}
                  className="group bg-white border border-brand-hairline hover:shadow-lift transition-shadow duration-300 h-full flex flex-col"
                >
                  <div className="relative overflow-hidden">
                    <img src={cs.image} alt={cs.title} className="w-full h-64 object-cover grayscale group-hover:grayscale-0 transition-all duration-700" loading="lazy" />
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-brand-slate/80">
                      {cs.category} · <time dateTime={cs.date}>{new Date(cs.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</time>
                    </div>
                    <h3 className="font-display text-2xl md:text-[26px] leading-tight text-brand-oxford mt-3">{cs.title}</h3>
                    <p className="mt-4 text-brand-slate leading-relaxed flex-1">{cs.excerpt}</p>
                    <div className="mt-6 flex items-end justify-between gap-4">
                      <p className="text-sm font-semibold text-brand-oxford">{cs.results}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-oxford border-b border-brand-oxford pb-1 group-hover:gap-3 transition-all">
                        Read the study <ArrowUpRight size={14} strokeWidth={1.5} />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-oxford text-brand-alabaster py-24 md:py-32">
        <div className="container-editorial grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-block w-10 h-px bg-brand-gold" aria-hidden />
              <span className="eyebrow text-brand-gold/90">Under NDA</span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05]">
              Request the full case-study dossier.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="cs-cta-contact"
              className="inline-flex items-center gap-3 bg-brand-alabaster text-brand-oxford px-8 py-5 text-[12px] font-semibold tracking-[0.16em] uppercase transition-colors hover:bg-brand-gold focus-ring"
            >
              Request Access <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CaseStudiesPage;
